import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { ITag } from '@fluentui/react';
import { Button, Divider, Field, Input, makeStyles } from '@fluentui/react-components';
import React, { useEffect, useRef } from 'react';
import { dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import ProcessSearchCompanyPicker from '../../processes/processSearch/ProcessSearchCompanyPicker';
import ProcessSearchFiscalYearPicker from '../../processes/processSearch/ProcessSearchFiscalYearPicker';
import StatUserPicker from '../../companies/list/StatUserPicker';
import DateRangePicker from '../../processes/processSearch/DateRangePicker';
import useDebounce from '../../../utils/hooks/UseDebounce';

export interface IDocumentAnalysisFilter {
  companyCodes?: string[];
  reportingPeriodEndFromDate?: Date;
  reportingPeriodEndToDate?: Date;
  selectedPeriodEndDateOption?: string;
  uploaderIds?: number[];
  uploadedFromDate?: string;
  uploadedToDate?: string;
  documentName?: string;
}

interface IDocumentAnalysisFilterProps {
  onResetFiltersClick: () => void;
  filterState: IDocumentAnalysisFilter;
  onUpdateState: (newState: IDocumentAnalysisFilter) => void;
}
const useStyles = makeStyles({
  filterPanel: { width: '300px' },
});

const DocumentAnalysisFilter = (props: IDocumentAnalysisFilterProps): JSX.Element => {
  const styles = dividerStyles();
  const classes = useStyles();
  const { onResetFiltersClick, filterState, onUpdateState } = props;
  const { companyCodes, uploaderIds, uploadedFromDate, uploadedToDate, documentName } = filterState;
  const [documentNameLocalState, setDocumentNameLocalState] = React.useState<string>(documentName);
  const isFirstRender = useRef(true);

  const debouncedSearchTerm = useDebounce(documentNameLocalState, 500);

  const onCompanyUpdate = (value: string[]) => {
    const newState = structuredClone(filterState);

    newState.companyCodes = value;
    onUpdateState(newState);
  };

  const onReportingPeriodDateRangeChange = (
    newStartDate: Date,
    newEndDate: Date,
    newSelectedOption: string,
  ) => {
    const newState = structuredClone(filterState);
    newState.reportingPeriodEndFromDate = newStartDate;
    newState.reportingPeriodEndToDate = newEndDate;
    newState.selectedPeriodEndDateOption = newSelectedOption;
    onUpdateState(newState);
  };

  const onUploaderChange = (value: number[]) => {
    const newState = structuredClone(filterState);

    newState.uploaderIds = value;
    onUpdateState(newState);
  };

  const onUploadDateRangeChange = (startDate: string, endDate: string) => {
    const newState = structuredClone(filterState);

    if (startDate && startDate !== newState.uploadedFromDate) {
      newState.uploadedFromDate = startDate;
    }

    if (endDate) {
      newState.uploadedToDate = endDate;
    }
    onUpdateState(newState);
  };

  const onDocumentNameChange = (newValue?: string) => {
    setDocumentNameLocalState(newValue);
  };

  useEffect(() => {
    // this is to prevent a state update and extra api call on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const newState = structuredClone(filterState);

    newState.documentName = debouncedSearchTerm;
    onUpdateState(newState);
  }, [debouncedSearchTerm]);

  return (
    <StackItem grow disableShrink className={classes.filterPanel}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <h2>Filter</h2>
        <Button onClick={onResetFiltersClick}>Reset Filters</Button>
      </Stack>
      <ProcessSearchCompanyPicker
        initialSelectedCodes={companyCodes ?? []}
        onCompanyUpdate={(items: ITag[]) => {
          onCompanyUpdate(items.map((item) => item.name.split(' ')[0].toString()));
        }}
      />
      <Divider className={styles.divider} />
      <ProcessSearchFiscalYearPicker
        label="Reporting Period End Date"
        initialSelectedOption={filterState.selectedPeriodEndDateOption}
        initialEndDate={filterState.reportingPeriodEndToDate}
        initialStartDate={filterState.reportingPeriodEndFromDate}
        onUpdateDate={onReportingPeriodDateRangeChange}
      />
      <Divider className={styles.divider} />
      <StatUserPicker
        label="Uploaded By"
        initialSelectedItems={uploaderIds ?? []}
        onInputFieldUpdate={onUploaderChange}
      />
      <Divider className={styles.divider} />
      <DateRangePicker
        label="Uploaded Date Range"
        initialStartDate={uploadedFromDate}
        initialEndDate={uploadedToDate}
        onUpdateDate={onUploadDateRangeChange}
      />
      <Divider className={styles.divider} />
      <Field label="Document Name">
        <Input
          aria-label="search documents by name"
          placeholder="Search documents"
          onChange={(e, data) => onDocumentNameChange(data.value)}
          value={documentNameLocalState}
        />
      </Field>
    </StackItem>
  );
};
export default DocumentAnalysisFilter;
